// mds-dojo — dijit/layout/LayoutContainer — FAITHFUL port of
// references/dijit/layout/LayoutContainer.js (overwrites the earlier freestyle
// flexbox version). AMD → ESM + classic declare from the faithful runtime; only
// the module's final `return LayoutContainer` becomes `export default`. Real
// measured absolute layout via ./utils.layoutChildren, not flexbox.
import array from '../../dojo/_base/array';
import declare from '../../dojo/_base/declare-runtime';
import domClass from '../../dojo/dom-class';
import domStyle from '../../dojo/dom-style';
import lang from '../../dojo/_base/lang';
import _WidgetBase from '../_WidgetBase';
import _LayoutWidget from './_LayoutWidget';
import layoutUtils from './utils';

var LayoutContainer: any = declare('dijit.layout.LayoutContainer', _LayoutWidget, {
  // summary:
  //		A LayoutContainer is a box with a specified size that contains a child
  //		widget marked region="center" and optionally edge children.

  // design: String — "headline" (default) or "sidebar"
  design: 'headline',

  baseClass: 'dijitLayoutContainer',

  startup: function () {
    if (this._started) {
      return;
    }
    array.forEach(this.getChildren(), this._setupChild, this);
    this.inherited(arguments);
  },

  _setupChild: function (child: any) {
    // Override _LayoutWidget._setupChild().
    this.inherited(arguments);

    var region = child.region;
    if (region) {
      domClass.add(child.domNode, this.baseClass + 'Pane');
    }
  },

  _getOrderedChildren: function () {
    // Return list of my children in the order that I want layoutChildren()
    // to process them (i.e. from the outside to the inside)
    var wrappers = array.map(this.getChildren(), function (this: any, child: any, idx: any) {
      return {
        pane: child,
        weight: [
          child.region == 'center' ? Infinity : 0,
          child.layoutPriority,
          (this.design == 'sidebar' ? 1 : -1) * (/top|bottom/.test(child.region) ? 1 : -1),
          idx
        ]
      };
    }, this);
    wrappers.sort(function (a: any, b: any) {
      var aw = a.weight, bw = b.weight;
      for (var i = 0; i < aw.length; i++) {
        if (aw[i] != bw[i]) {
          return aw[i] - bw[i];
        }
      }
      return 0;
    });

    return array.map(wrappers, function (w: any) { return w.pane; });
  },

  layout: function () {
    layoutUtils.layoutChildren(this.domNode, this._contentBox, this._getOrderedChildren());
  },

  addChild: function (child: any, insertIndex: any) {
    this.inherited(arguments);
    if (this._started) {
      this.layout();
    }
  },

  removeChild: function (child: any) {
    this.inherited(arguments);
    if (this._started) {
      this.layout();
    }

    // Clean up whatever style changes we made to the child pane.
    domClass.remove(child.domNode, this.baseClass + 'Pane');
    domStyle.set(child.domNode, {
      top: 'auto',
      bottom: 'auto',
      left: 'auto',
      right: 'auto',
      position: 'static'
    });
    domStyle.set(child.domNode, /top|bottom/.test(child.region) ? 'width' : 'height', 'auto');
  }
});

LayoutContainer.ChildWidgetProperties = {
  // summary:
  //		These properties can be specified for the children of a LayoutContainer.

  // region: [const] String
  region: '',

  // layoutAlign: [const deprecated] String — synonym for region ("client" == "center")
  layoutAlign: '',

  // layoutPriority: [const] Number
  layoutPriority: 0
};

// Since any widget can be specified as a LayoutContainer child, mix it into the
// base widget class. (Hack, but effective — for the benefit of the parser.)
lang.extend(_WidgetBase, LayoutContainer.ChildWidgetProperties);

export default LayoutContainer;
